import {
  BOARD,
  RADIUS_OPTIONS,
  RANKING,
  TIME_RANGES,
  type RadiusId,
  type TimeRangeId,
} from './constants';
import { distanceKm, geohashCenter } from './geo';
import { decay } from './ranking';
import type { AreaDoc, LatLng, TileSummary } from './types';

/** A post as shown on the viewer's board. */
export interface BoardPost extends TileSummary {
  /** Hot score decayed to the viewer's clock. */
  score: number;
  /** Distance from the viewer's centre in km, when both locations are known. */
  distanceKm: number | null;
  /** New and not ranked yet: shown as a small tile until people open it. */
  fresh?: boolean;
  /** A paid post (see promotions.ts): whispered more, marked Sponsored. */
  sponsored?: boolean;
}

export interface ViewOptions {
  center: LatLng | null;
  /** null = the whole world. */
  radiusKm: number | null;
  now: number;
  /** Only posts made at or after this time (ms), when set: the reader's time range. */
  since?: number;
}

/**
 * Whether a post is in the reader's circle, and how far away it is. International AI summaries
 * (an agent's post with no location) are news for everyone: they're in every view.
 */
function placeInView(
  g5: string | null,
  view: ViewOptions,
  isAgent = false,
): { ok: boolean; distanceKm: number | null } {
  const d = g5 && view.center ? distanceKm(view.center, geohashCenter(g5)) : null;
  if (view.radiusKm === null || (!g5 && isAgent)) return { ok: true, distanceKm: d };
  return { ok: d !== null && d <= view.radiusKm, distanceKm: d };
}

function summary(t: TileSummary): TileSummary {
  return {
    id: t.id,
    title: t.title,
    excerpt: t.excerpt ?? '',
    thumb: t.thumb,
    author: t.author,
    isAgent: t.isAgent,
    g5: t.g5,
    createdAt: t.createdAt,
  };
}

/**
 * Merges the area boards covering the viewer's circle into one ranked list. Each doc's scores are
 * decayed from its own `rankedAt`, which keeps docs written at different times comparable.
 */
export function mergeBoard(
  docs: readonly AreaDoc[],
  view: ViewOptions & { limit: number },
): BoardPost[] {
  const byId = new Map<string, BoardPost>();
  for (const doc of docs) {
    for (const t of doc.tiles ?? []) {
      if (view.since !== undefined && t.createdAt < view.since) continue;
      const score = decay(t.score, doc.rankedAt, view.now);
      if (score < RANKING.minScore) continue;
      const place = placeInView(t.g5, view, t.isAgent);
      if (!place.ok) continue;
      const prev = byId.get(t.id);
      if (prev && prev.score >= score) continue;
      byId.set(t.id, { ...summary(t), score, distanceKm: place.distanceKm });
    }
  }
  return [...byId.values()]
    .sort((a, b) => b.score - a.score || b.createdAt - a.createdAt)
    .slice(0, view.limit);
}

/**
 * Voices and whispers. Voices are the posts people are opening (score at least
 * `BOARD.voiceMinScore`), most opened first: they make the board. Whispers are the rest, newest
 * first: new posts nobody has opened yet and posts that have faded. The reader hears them on
 * demand, and opening one is how it gets its voice. With too few voices, whispers are moved up to
 * fill the middle, but never the `paid` ones (they're whispered as Sponsored instead).
 */
export function splitVoices(
  ranked: readonly BoardPost[],
  latest: readonly BoardPost[],
  paid: ReadonlySet<string> = new Set(),
): { voices: BoardPost[]; whispers: BoardPost[] } {
  const voices = ranked.filter((p) => p.score >= BOARD.voiceMinScore);
  const loud = new Set(voices.map((p) => p.id));
  const seen = new Set<string>();
  let whispers = [...latest, ...ranked].filter((p) => {
    if (loud.has(p.id) || seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
  // There's always a main talk: with too few voices, the most promising whispers (the most
  // opened, then the newest) speak up.
  if (voices.length < BOARD.minVoices) {
    // Never a paid post: it's whispered as Sponsored, and speaks up only when people open it.
    const promoted = whispers
      .filter((p) => !paid.has(p.id))
      .sort((a, b) => b.score - a.score || b.createdAt - a.createdAt)
      .slice(0, BOARD.minVoices - voices.length);
    const up = new Set(promoted.map((p) => p.id));
    voices.push(...promoted.map((p) => ({ ...p, fresh: false })));
    whispers = whispers.filter((p) => !up.has(p.id));
  }
  whispers.sort((a, b) => b.createdAt - a.createdAt);
  return { voices, whispers };
}

/** Merges the newest posts of the covering area boards (newest first). */
export function mergeLatest(
  docs: readonly AreaDoc[],
  view: ViewOptions & { limit: number },
): BoardPost[] {
  const byId = new Map<string, BoardPost>();
  for (const doc of docs) {
    for (const t of doc.latest ?? []) {
      if (view.now - t.createdAt > BOARD.latestMaxAgeMs || byId.has(t.id)) continue;
      if (view.since !== undefined && t.createdAt < view.since) continue;
      const place = placeInView(t.g5, view, t.isAgent);
      if (!place.ok) continue;
      byId.set(t.id, { ...summary(t), score: 0, distanceKm: place.distanceKm });
    }
  }
  return [...byId.values()].sort((a, b) => b.createdAt - a.createdAt).slice(0, view.limit);
}

export function radiusOption(id: RadiusId) {
  return RADIUS_OPTIONS.find((o) => o.id === id) ?? RADIUS_OPTIONS[RADIUS_OPTIONS.length - 1]!;
}

export function timeRange(id: TimeRangeId) {
  return TIME_RANGES.find((o) => o.id === id) ?? TIME_RANGES[TIME_RANGES.length - 1]!;
}

/** The next longer time range, or null at the longest. */
export function longerTimeRange(id: TimeRangeId): TimeRangeId | null {
  const i = TIME_RANGES.findIndex((o) => o.id === id);
  return i >= 0 && i < TIME_RANGES.length - 1 ? TIME_RANGES[i + 1]!.id : null;
}

/** The next wider radius, or null at World. */
export function widerRadius(id: RadiusId): RadiusId | null {
  const i = RADIUS_OPTIONS.findIndex((o) => o.id === id);
  return i >= 0 && i < RADIUS_OPTIONS.length - 1 ? RADIUS_OPTIONS[i + 1]!.id : null;
}
